import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Sparkles, Search, Anchor, Calendar, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import LiveTicker from "@/components/LiveTicker";

export default function Home() {
  const { t, localizePath, lang } = useI18n();
  const navigate = useNavigate();

  const tips = [
    { icon: Wallet, key: "tip1", img: "https://images.pexels.com/photos/29931104/pexels-photo-29931104.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=720" },
    { icon: Compass, key: "tip2", img: "https://images.pexels.com/photos/4147762/pexels-photo-4147762.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=720" },
    { icon: Calendar, key: "tip3", img: "https://images.pexels.com/photos/28096708/pexels-photo-28096708.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=720" },
  ];

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img
          src="https://images.pexels.com/photos/8299833/pexels-photo-8299833.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="Luxury yacht aerial view"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 lg:px-10 pb-20 lg:pb-28 pt-32 grain">
          <p className="label-eyebrow text-gold animate-reveal" data-testid="home-eyebrow">
            {t("home.eyebrow")}
          </p>
          <h1
            data-testid="hero-title"
            className="mt-5 font-serif text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] max-w-5xl animate-fade-up"
          >
            {t("home.h1_part1")}{" "}
            <em className="italic text-gold font-medium">{t("home.h1_part2")}</em>
            {t("home.h1_part3")}
          </h1>
          <p
            className="mt-6 max-w-2xl text-white/85 text-base lg:text-lg leading-relaxed animate-fade-up"
            style={{ animationDelay: "0.15s" }}
          >
            {t("home.subtitle")}
          </p>

          <div className="mt-9 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Button
              data-testid="hero-cta-primary"
              onClick={() => navigate(localizePath("/search"))}
              className="bg-white text-navy hover:bg-sand rounded-full h-12 px-7 text-sm font-semibold"
            >
              <Search className="h-4 w-4 mr-2" /> {t("home.ctaPrimary")}
            </Button>
            <Button
              data-testid="hero-cta-secondary"
              onClick={() => navigate(localizePath("/come-funziona"))}
              variant="outline"
              className="bg-transparent border-white/60 text-white hover:bg-white/10 rounded-full h-12 px-7 text-sm font-semibold"
            >
              {t("home.ctaSecondary")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          <div className="mt-14 hidden md:flex items-center gap-8 text-white/70 text-xs uppercase tracking-[0.25em]">
            <span>{t("home.strip1")}</span>
            <span>·</span>
            <span>{t("home.strip2")}</span>
            <span>·</span>
            <span>{t("home.strip3")}</span>
          </div>

          <LiveTicker />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="label-eyebrow">{t("home.howEyebrow")}</p>
              <h2 className="mt-3 font-serif text-3xl lg:text-5xl text-navy tracking-tight">
                {t("home.howTitle")}
              </h2>
            </div>
            <ol className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-[#E2E8F0] rounded-2xl overflow-hidden border border-[#E2E8F0]">
              {[1, 2, 3, 4].map((i) => (
                <li
                  key={i}
                  data-testid={`how-step-${i}`}
                  className="bg-white p-8 flex flex-col"
                >
                  <span className="font-serif text-5xl text-gold/80 leading-none">0{i}</span>
                  <h3 className="mt-6 font-sans font-medium text-lg text-navy">
                    {t(`home.step${i}Title`)}
                  </h3>
                  <p className="mt-2 text-sm text-ink leading-relaxed">
                    {t(`home.step${i}Desc`)}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section className="py-24 lg:py-28 bg-sand">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">{t("home.whyEyebrow")}</p>
            <h2 className="mt-3 font-serif text-3xl lg:text-5xl text-navy tracking-tight">
              {t("home.whyTitle")}
            </h2>
            <p className="mt-5 text-ink leading-relaxed">{t("home.whyP1")}</p>
            <Button
              data-testid="why-cta"
              onClick={() => navigate(localizePath("/search"))}
              className="mt-8 bg-navy text-white hover:bg-[#06101f] rounded-md h-11 px-6"
            >
              {t("home.ctaPrimary")}
            </Button>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {[
              { v: t("home.stat1Value"), l: t("home.stat1Label"), i: Sparkles },
              { v: t("home.stat2Value"), l: t("home.stat2Label"), i: Anchor },
              { v: t("home.stat3Value"), l: t("home.stat3Label"), i: Search },
              { v: t("home.stat4Value"), l: t("home.stat4Label"), i: Compass },
            ].map((stat, idx) => (
              <div
                key={idx}
                className="bg-white border border-[#E2E8F0] rounded-2xl p-6 flex flex-col justify-between min-h-[160px]"
              >
                <stat.i className="h-5 w-5 text-turquoise" />
                <div>
                  <p className="font-serif text-4xl text-navy">{stat.v}</p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-ink">{stat.l}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SMART TIPS */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-3xl">
            <p className="label-eyebrow">{t("home.smartEyebrow")}</p>
            <h2 className="mt-3 font-serif text-3xl lg:text-5xl text-navy tracking-tight">
              {t("home.smartTitle")}
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {tips.map((tip, idx) => (
              <article
                key={tip.key}
                data-testid={`smart-tip-${idx}`}
                className="group rounded-2xl overflow-hidden border border-[#E2E8F0] bg-white"
              >
                <div className="relative h-56">
                  <img src={tip.img} alt={t(`home.${tip.key}Title`)} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 chip bg-white/95 border-0 text-navy uppercase tracking-wider text-[10px] font-semibold">
                    <tip.icon className="h-3 w-3 text-turquoise" /> {idx === 0 ? t("home.chipPartners") : idx === 1 ? t("home.chipRoutes") : t("home.chipService")}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-navy leading-tight">
                    {t(`home.${tip.key}Title`)}
                  </h3>
                  <p className="mt-2 text-sm text-ink leading-relaxed">
                    {t(`home.${tip.key}Desc`)}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-14 flex justify-center">
            <Button
              data-testid="bottom-cta"
              onClick={() => navigate(localizePath("/search"))}
              className="bg-navy text-white hover:bg-[#06101f] rounded-full h-12 px-7 text-sm font-semibold"
            >
              {t("home.ctaPrimary")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
      {/* REVIEWS */}
      <section className="bg-sand py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow text-center">{lang === "en" ? "What guests say" : "Cosa dicono i nostri clienti"}</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight text-center max-w-3xl mx-auto leading-tight">
            {lang === "en"
              ? "Quietly chosen by travellers across Europe and the world."
              : "Le voci di chi ha scelto Seamatch."}
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                stars: 5,
                text_it: "Non sapevamo quale destinazione scegliere. Seamatch ha organizzato una settimana perfetta alle Eolie, con un catamarano impeccabile e un consulente sempre presente.",
                text_en: "Seamatch handled every detail and crafted an unforgettable week in the Aeolian Islands. Their concierge was on hand the entire time — a truly tailor-made experience.",
                name: "Marco R.", country_it: "Italia", country_en: "Italy", flag: "🇮🇹",
              },
              {
                stars: 5,
                text_it: "Avevamo solo qualche idea generica. Loro hanno costruito un itinerario su misura in Sardegna, fuori dai circuiti turistici. Tornerò sicuramente.",
                text_en: "We only had a few rough ideas. Seamatch designed an outstanding itinerary in Sardinia, well off the tourist trail. We will absolutely return next season.",
                name: "Sarah M.", country_it: "USA", country_en: "USA", flag: "🇺🇸",
              },
              {
                stars: 5,
                text_it: "Esperienza eccellente. Comunicazione chiara, preventivo trasparente, equipaggio professionale. Esattamente come vorrei che funzionasse un servizio premium.",
                text_en: "Outstanding from first message to disembarkation. Clear pricing, transparent communication, professional crew. Exactly how a premium service should work.",
                name: "Lars H.", country_it: "Germania", country_en: "Germany", flag: "🇩🇪",
              },
              {
                stars: 5,
                text_it: "Pensavamo che una vacanza in barca fosse fuori dal nostro budget. Seamatch ci ha aggregati a un altro gruppo simile e abbiamo vissuto una settimana incredibile in Grecia.",
                text_en: "Seamatch surprised us with a carefully curated cabin-charter in the Cyclades. Beautiful yacht, like-minded guests on board, and zero stress. Highly recommended.",
                name: "Eva P.", country_it: "Paesi Bassi", country_en: "Netherlands", flag: "🇳🇱",
              },
              {
                stars: 5,
                text_it: "Squadra molto professionale. Ci hanno consigliato di spostare le date di una settimana per risparmiare il 25% e ottenere una barca migliore. Onesti.",
                text_en: "Refreshingly honest. They suggested moving our dates by a week and unlocked a much better yacht for 25% less. Real concierge attitude.",
                name: "Anders K.", country_it: "Norvegia", country_en: "Norway", flag: "🇳🇴",
              },
              {
                stars: 5,
                text_it: "Una vacanza in Croazia indimenticabile. Lo skipper era un amico di famiglia ormai. Grazie Seamatch.",
                text_en: "An unforgettable week in Croatia. By the end the skipper felt like a family friend. Thank you, Seamatch — we will be back.",
                name: "Giulia F.", country_it: "Italia", country_en: "Italy", flag: "🇮🇹",
              },
            ].map((r, idx) => (
              <article
                key={idx}
                data-testid={`review-card-${idx}`}
                className="bg-white rounded-2xl border border-[#E5E1D2] p-7 shadow-sm hover:shadow-md transition"
              >
                <div className="flex gap-0.5 text-gold text-base" aria-label={`${r.stars} stars`}>
                  {Array.from({ length: r.stars }).map((_, i) => (<span key={i}>★</span>))}
                </div>
                <p className="mt-4 font-serif text-base text-navy leading-relaxed">
                  &ldquo;{lang === "en" ? r.text_en : r.text_it}&rdquo;
                </p>
                <div className="mt-5 flex items-center gap-2 text-sm">
                  <span className="text-base">{r.flag}</span>
                  <span className="font-semibold text-navy">{r.name}</span>
                  <span className="text-ink/50">— {lang === "en" ? r.country_en : r.country_it}</span>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-10 text-center text-xs text-ink/50">
            {lang === "en"
              ? "Selected reviews from real Seamatch guests. Names and photos are presented with their consent."
              : "Recensioni di clienti Seamatch reali. Nomi e immagini sono pubblicati con il loro consenso."}
          </p>
        </div>
      </section>
    </div>
  );
}
